/**
 * チャットボットの選択式シナリオ。
 *
 * ヘルプ記事 (src/pages/HelpPage/contents/contents.tsx) と同じ事実をもとにしている。
 * 片方だけ直すと食い違うので、フローや価格が変わったときは両方直す。
 *
 * 回答は clientId の有無でログイン状態を分岐する。clientId があれば
 * 「ログイン済みかつクライアント選択済み」なので、具体的なリンクを出す。
 */

export type FaqAnswer = {
  /** 本文。改行は \n で表現し、表示側で分割する */
  text: (clientId?: string) => string
  /** 本文のあとに出すリンク。未ログイン時は to が null のものを出さない */
  links?: (clientId?: string) => { label: string; to: string }[]
}

export type FaqNode = {
  id: string
  /** 選択肢として表示する文言 */
  label: string
  /** 子を持つ場合は分岐、持たない場合は answer が必須 */
  children?: FaqNode[]
  answer?: FaqAnswer
}

const helpLink = (id: string, label: string) => ({ label, to: `/help/${id}` })

export const faqRoot: FaqNode[] = [
  {
    id: 'start',
    label: '利用を始めたい',
    children: [
      {
        id: 'start-how',
        label: '利用開始までの流れを知りたい',
        answer: {
          text: () =>
            'ワナップは利用申請を承認させていただいた方からご利用いただけます。\n' +
            '1. 利用申請フォームからお申し込み\n' +
            '2. 運営が承認するとメールで通知が届きます\n' +
            '3. ログインできるようになります\n' +
            '4. デバイスをご購入いただきます\n' +
            '5. 届いたデバイスを登録し、通知を設定します',
          links: () => [{ label: '利用申請フォーム', to: '/inquiry/register' }, helpLink('getting-started', '利用開始のやり方（詳細）')],
        },
      },
      {
        id: 'start-code',
        label: '招待コードを持っている',
        answer: {
          text: () => '招待コード付きの申請フォームからお申し込みいただくと、承認を待たずにその場で登録が完了します。',
          links: () => [{ label: '招待コード付きの申請フォーム', to: '/invitation' }],
        },
      },
      {
        id: 'start-waiting',
        label: '申請したのに承認の連絡が来ない',
        answer: {
          text: () =>
            '承認されると運営からメールで通知が届きます。迷惑メールフォルダに入っていないかご確認ください。\n' +
            'しばらく待っても届かない場合は、お問い合わせからご連絡ください。',
          links: (clientId) => (clientId ? [{ label: 'お問い合わせ', to: `/clients/${clientId}/inquiries` }] : [{ label: 'お問い合わせ', to: '/inquiry' }]),
        },
      },
      {
        id: 'start-login',
        label: 'ログインの方法がわからない',
        answer: {
          text: () =>
            'ログイン方法は2通りあります。\n' +
            '・メールアドレスを入力して、届いたログイン用リンクを開く\n' +
            '・Googleアカウントでログインする\n' +
            'いずれも、利用申請が承認されたあとにご利用いただけます。',
          links: () => [{ label: 'ログインページ', to: '/auth/login' }],
        },
      },
    ],
  },
  {
    id: 'purchase',
    label: 'デバイスを購入したい',
    children: [
      {
        id: 'purchase-how',
        label: '購入の手順を知りたい',
        answer: {
          text: (clientId) =>
            clientId
              ? '購入ページでデバイスを選び、数量と送付先をご入力ください。\nご注文いただくと請求書が発行されます。お支払い期限は発行から7日間です。\nお支払いの確認後、順次発送いたします。'
              : 'デバイスのご購入にはログインが必要です。\nまだアカウントをお持ちでない場合は、まず利用申請からお願いします。\n\nご購入の流れは、購入ページでデバイスを選び、数量と送付先をご入力いただくと請求書が発行され、お支払い後に発送となります。',
          links: (clientId) =>
            clientId
              ? [{ label: 'デバイスの購入ページ', to: `/clients/${clientId}/orders` }, helpLink('purchase', 'デバイスの購入方法（詳細）')]
              : [{ label: '利用申請フォーム', to: '/inquiry/register' }, helpLink('purchase', 'デバイスの購入方法（詳細）')],
        },
      },
      {
        id: 'purchase-payment',
        label: '支払い方法と期限を知りたい',
        answer: {
          text: () => 'ご注文いただくと請求書が発行されます。お支払い期限は発行から7日間です。\n送料は注文内容に応じて加算されます。',
          links: (clientId) =>
            clientId ? [{ label: 'デバイスの購入ページ', to: `/clients/${clientId}/orders` }] : [helpLink('purchase', 'デバイスの購入方法')],
        },
      },
      {
        id: 'purchase-stock',
        label: '在庫がない / 大量に注文したい',
        answer: {
          text: () => '在庫がない場合や、大量にご注文いただく場合は受注生産となります。\nお問い合わせよりご相談ください。納期をあわせてご案内します。',
          links: (clientId) => (clientId ? [{ label: 'お問い合わせ', to: `/clients/${clientId}/inquiries` }] : [{ label: 'お問い合わせ', to: '/inquiry' }]),
        },
      },
      {
        id: 'purchase-status',
        label: '注文した商品の発送状況を知りたい',
        answer: {
          text: (clientId) =>
            clientId
              ? '購入ページの注文履歴から、発送状況をご確認いただけます。'
              : 'ログインしていただくと、購入ページの注文履歴から発送状況をご確認いただけます。',
          links: (clientId) => (clientId ? [{ label: '注文履歴', to: `/clients/${clientId}/orders` }] : [{ label: 'ログインページ', to: '/auth/login' }]),
        },
      },
    ],
  },
  {
    id: 'notification',
    label: '通知の設定をしたい',
    children: [
      {
        id: 'notification-line',
        label: 'LINEで通知を受け取りたい',
        answer: {
          text: (clientId) =>
            (clientId
              ? 'プロフィール設定を開いてLINE通知をONにします。\n'
              : 'ログインしたうえで、右上のアイコンからプロフィール設定を開き、LINE通知をONにします。\n') +
            '表示されるQRコードからワナップ公式LINEを友だち登録し、ページ下部の連携コードをコピーして公式LINEに送信してください。',
          links: (clientId) =>
            clientId
              ? [{ label: 'プロフィール設定', to: `/clients/${clientId}/profile` }, helpLink('line', 'LINE通知の設定方法（詳細）')]
              : [helpLink('line', 'LINE通知の設定方法（詳細）')],
        },
      },
      {
        id: 'notification-group',
        label: 'グループ全体の通知をトークルームに送りたい',
        answer: {
          text: (clientId) =>
            (clientId ? 'グループの画面の「LINE通知先の設定」から行います。\n' : 'ログイン後、グループの画面の「LINE通知先の設定」から行います。\n') +
            'そのトークルームに公式アカウントを招待し、画面に表示された連携コードを送信してください。個人用の連携コードとは別のコードです。\n' +
            'この設定は、ご利用形態がグループの場合のみ、かつグループの管理者のみ行えます。個人（ユーザー）でのご利用では設定できません。\n' +
            'グループ全体の通知先はひとつのトークルームだけです。変更するには先に「連携を解除する」から解除してください。',
          links: (clientId) =>
            clientId
              ? [{ label: 'グループの画面', to: `/clients/${clientId}` }, helpLink('line', 'LINE通知の設定方法（詳細）')]
              : [helpLink('line', 'LINE通知の設定方法（詳細）')],
        },
      },
      {
        id: 'notification-subgroup',
        label: 'サブグループ単位で通知を送りたい',
        answer: {
          text: (clientId) =>
            (clientId ? 'サブグループの画面の「LINE通知先の設定」から行います。\n' : 'ログイン後、サブグループの画面の「LINE通知先の設定」から行います。\n') +
            'サブグループに登録したデバイスの通知だけが、そのトークルームに届きます。\n' +
            'サブグループは、ご利用形態がグループの場合のみ利用できます。個人（ユーザー）でのご利用では使えません。\n' +
            'さらにサブグループ単位の通知は Proプラン の機能です。Proプランでない場合、設定しても通知は送られません。\n' +
            '設定・編集にはグループの管理者権限も必要です。',
          links: (clientId) =>
            clientId
              ? [{ label: 'サブグループ', to: `/clients/${clientId}/subGroups` }, helpLink('line', 'LINE通知の設定方法（詳細）')]
              : [helpLink('line', 'LINE通知の設定方法（詳細）')],
        },
      },
      {
        id: 'notification-release',
        label: '通知先を解除したい',
        answer: {
          text: () =>
            '解除する方法は2つあります。\n' +
            '・各設定画面の「連携を解除する」から解除する\n' +
            '・そのトークルームからワナップ公式アカウントを退出させる、または公式アカウントをブロックする\n' +
            '退出・ブロックした場合は、そのトークルームに紐づいていた通知先（個人・グループ全体・サブグループ）がすべて解除されます。',
          links: () => [helpLink('line', 'LINE通知の設定方法')],
        },
      },
      {
        id: 'notification-duplicated',
        label: '同じ通知が複数回届く',
        answer: {
          text: () =>
            'グループ全体の通知先は、サブグループの設定とは独立して常に通知が送られます。\n' +
            'グループ全体とサブグループで別のトークルームを指定していると、対象のデバイスが作動したとき両方に届きます。\n' +
            '片方を解除するか、同じトークルームを指定してください。',
          links: () => [helpLink('line', 'LINE通知の設定方法')],
        },
      },
    ],
  },
  {
    id: 'other',
    label: 'ここに無いことを聞きたい',
    answer: {
      text: () =>
        'このヘルプでは、利用開始・購入・通知・デバイスの使い方についてご案内しています。\n' +
        'それ以外のご質問、個別のご契約やご注文の状況、法令の判断が必要なことについては、\n' +
        'お問い合わせから直接ご連絡ください。担当者が確認してご返信します。',
      links: (clientId) => (clientId ? [{ label: 'お問い合わせ', to: `/clients/${clientId}/inquiries` }] : [{ label: 'お問い合わせ', to: '/inquiry' }]),
    },
  },
  {
    id: 'device',
    label: 'デバイスの使い方を知りたい',
    children: [
      {
        id: 'device-register',
        label: '届いたデバイスを登録したい',
        answer: {
          text: (clientId) =>
            clientId
              ? 'デバイスに同梱のご案内に従って登録してください。登録後はデバイス一覧に表示され、作動状況を確認できるようになります。'
              : 'デバイスに同梱のご案内に従って登録してください。登録にはログインが必要です。',
          links: (clientId) => (clientId ? [{ label: 'デバイス一覧', to: `/clients/${clientId}/devices` }] : [{ label: 'ログインページ', to: '/auth/login' }]),
        },
      },
      {
        id: 'device-status',
        label: 'デバイスの作動状況を確認したい',
        answer: {
          text: (clientId) =>
            clientId
              ? 'デバイス一覧から、各デバイスの作動状況・最終通信・異常検知をご確認いただけます。\nデバイスをクリックすると詳細が表示されます。'
              : 'ログインしていただくと、デバイス一覧から作動状況・最終通信・異常検知をご確認いただけます。',
          links: (clientId) => (clientId ? [{ label: 'デバイス一覧', to: `/clients/${clientId}/devices` }] : [{ label: 'ログインページ', to: '/auth/login' }]),
        },
      },
    ],
  },
]

/** id から節点を引く（経路探索用） */
export const findFaqNode = (nodes: FaqNode[], id: string): FaqNode | undefined => {
  for (const node of nodes) {
    if (node.id === id) return node
    const hit = node.children && findFaqNode(node.children, id)
    if (hit) return hit
  }
  return undefined
}
